import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { gsap } from 'gsap';
import './CardNav.css';

const CardNav = ({
  items = [],
  onLogin,
  loginLabel = 'Ingresar',
  logo,
  logoAlt = 'Logo',
  className = '',
  baseColor = 'background: linear-gradient(180deg, #4f525a 0%, #101b2f 100%);',
  buttonBgColor = '#eb673b',
  buttonTextColor = '#ffffff',
  menuColor = '#ffffff',
  openMenuColor = '#ffffff',
  accentColor = '#eb673b',
  position = 'left',
  ease = 'power3.out',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const navRef = useRef(null);
  const panelRef = useRef(null);
  const cardRefs = useRef([]);
  const tlRef = useRef(null);

  const totalLinks = useMemo(
    () => items.reduce((count, item) => count + (item.links?.length || 0), 0),
    [items]
  );

  useLayoutEffect(() => {
    const navEl = navRef.current;
    const panelEl = panelRef.current;
    if (!navEl || !panelEl) return undefined;

    const ctx = gsap.context(() => {
      gsap.set(navEl, { opacity: 1 });
      gsap.set(panelEl, { height: 0, opacity: 0, overflow: 'hidden' });
      gsap.set(cardRefs.current.filter(Boolean), { y: 28, opacity: 0 });
    }, navEl);

    return () => ctx.revert();
  }, [items]);

  const buildTimeline = () => {
    const panelEl = panelRef.current;
    if (!panelEl) return null;

    tlRef.current?.kill();

    const cards = cardRefs.current.filter(Boolean);
    const tl = gsap.timeline({ paused: true, defaults: { ease } });

    tl.to(panelEl, {
      height: () => panelEl.scrollHeight,
      opacity: 1,
      duration: 0.45,
    });

    if (cards.length) {
      tl.to(cards, {
        y: 0,
        opacity: 1,
        duration: 0.55,
        stagger: 0.08,
      }, '-=0.12');
    }

    tlRef.current = tl;
    return tl;
  };

  const openMenu = () => {
    const tl = buildTimeline();
    if (!tl) return;
    setIsOpen(true);
    tl.play(0);
  };

  const closeMenu = () => {
    const tl = tlRef.current || buildTimeline();
    if (!tl) return;
    tl.eventCallback('onReverseComplete', () => setIsOpen(false));
    tl.reverse();
  };

  const toggleMenu = () => {
    if (isOpen) closeMenu();
    else openMenu();
  };

  const setCardRef = (index) => (el) => {
    if (el) cardRefs.current[index] = el;
  };

  return (
    <div
      ref={navRef}
      className={`card-nav-container ${className} ${position === 'right' ? 'is-right' : 'is-left'} ${isOpen ? 'open' : ''}`.trim()}
      style={{ '--card-nav-base': baseColor, '--card-nav-accent': accentColor, '--card-nav-button': buttonBgColor, '--card-nav-button-text': buttonTextColor }}
    >
      <div className="card-nav-shell">
        <button
          type="button"
          className="card-nav-toggle"
          onClick={toggleMenu}
          aria-label={isOpen ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={isOpen}
          style={{ color: isOpen ? openMenuColor : menuColor }}
        >
          <span />
          <span />
          <span />
        </button>

        <div className="card-nav-brand">
          {logo ? (
            <img src={logo} alt={logoAlt} className="card-nav-logo" />
          ) : (
            <>
              <span className="card-nav-kicker">KER</span>
              <span className="card-nav-title">Mantenimiento Spa</span>
            </>
          )}
        </div>

        <button
          type="button"
          className="card-nav-cta"
          onClick={onLogin}
          
        >
          {loginLabel}
        </button>
      </div>

      <div ref={panelRef} className="card-nav-panel" aria-hidden={!isOpen}>
        <div className="card-nav-grid">
          {items.map((item, index) => (
            <article
              className="card-nav-card"
              key={`${item.label}-${index}`}
              ref={setCardRef(index)}
              style={{ backgroundColor: item.bgColor, color: item.textColor }}
            >
              <div className="card-nav-card-label">{item.label}</div>
              <div className="card-nav-card-links">
                {item.links?.map((link) => {
                  if (link.onClick) {
                    return (
                      <button
                        key={link.label}
                        type="button"
                        className="card-nav-link"
                        onClick={(event) => {
                          closeMenu();
                          link.onClick(event);
                        }}
                      >
                        <span>{link.label}</span>
                        <i />
                      </button>
                    );
                  }

                  return (
                    <a
                      key={link.label}
                      href={link.href}
                      aria-label={link.ariaLabel || link.label}
                      className="card-nav-link"
                      onClick={closeMenu}
                    >
                      <span>{link.label}</span>
                      <i />
                    </a>
                  );
                })}
              </div>
            </article>
          ))}
        </div>
        <div className="card-nav-footer">
          <span>{totalLinks} accesos rápidos</span>
        </div>
      </div>
    </div>
  );
};

export default CardNav;
